import { h, Fragment } from 'preact'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'preact/compat'
import { omnioBalanceCheck } from '../../api/omnio'
import { debounce } from '../../helpers/common'
import {
  pageStateSelector,
  pageStateUpdate,
} from '../../redux/slice/pageStateSlice'
import { clearAuthoriseData } from '../../redux/slice/paymentResponseSlice'
import {
  balanceCheckError,
  prePaymentSelector,
  updateBalanceData,
  updateBalancePending,
} from '../../redux/slice/prePaymentSlice'
import ExpirationDate from './Fields/ExpirationDate'
import CVV from './Fields/CVV'
import CardNumber from './Fields/CardNumber'
import CardholderName from './Fields/CardholderName'
import SaveCard from './Fields/SaveCard'
import Email from './Fields/Email'
import { CARD_FIELDS } from '../../constants/card'
import { useTranslations } from '../../hooks/useTranslations'
import './styles.scss'
import useCardType from './hooks/useCardType'
import { transactionDetailsSelector } from '../../redux/slice/transactionDetailsSlice'
import { selectIsPciFormEnabled } from '../../redux/selectors/evervault'

const ManualCardForm = ({
  register,
  setValue,
  setFocus,
  watch,
  errors,
  control,
  trigger,
  clearErrors,
  isMotoTransaction,
  billingAddress,
  canSaveCard = false,
  shouldSaveCard = false,
  hideCardHolderName = false,
  getValues,
}) => {
  const cardNumberWatch = watch(CARD_FIELDS.number)
  const expirationMonthWatch = watch(CARD_FIELDS.expirationMonth)
  const expirationYearWatch = watch(CARD_FIELDS.expirationYear)
  const cvvWatch = watch(CARD_FIELDS.cvv)
  const cardType = useCardType(cardNumberWatch)
  const [cardBrand, setCardBrand] = useState('')
  const l10n = useTranslations()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const isPciFormEnabled = useSelector(selectIsPciFormEnabled)
  const prePayment = useSelector(prePaymentSelector)
  const pageState = useSelector(pageStateSelector)
  const dispatch = useDispatch()

  const previousOmnioValue = pageState.isCardNumberOmnio

  const checkOmnioData = async () => {
    const trimmedCardNumber = cardNumberWatch?.replaceAll(' ', '')

    const isCardNumberOmnio =
      trimmedCardNumber?.length === 16 &&
      /^(40006506)\d{5,}$/.test(trimmedCardNumber.replace(/\s/g, ''))

    if (previousOmnioValue) {
      dispatch(clearAuthoriseData())

      if (isPciFormEnabled) {
        window.parent?.postMessage?.(
          {
            type: 'MFEStoreDispatch',
            data: {
              actionType: 'clearAuthoriseData',
            },
          },
          '*',
        )
      }
    }

    if (isPciFormEnabled) {
      window.parent?.postMessage?.(
        {
          type: 'MFEStoreDispatch',
          data: {
            actionType: 'PaymentsMFEIsCardNumberOmnio',
            message: isCardNumberOmnio,
          },
        },
        '*',
      )
    }

    dispatch(
      pageStateUpdate({
        isCardNumberOmnio,
      }),
    )

    if (
      !cardNumberWatch ||
      !expirationMonthWatch ||
      !expirationYearWatch ||
      !cvvWatch
    ) {
      return
    }

    const isCVVOmnio =
      cvvWatch.toString().length === 3 || cvvWatch.toString().length === 4

    if (
      isCardNumberOmnio &&
      isCVVOmnio &&
      expirationMonthWatch.length >= 2 &&
      expirationYearWatch.length >= 2
    ) {
      if (isPciFormEnabled) {
        window.parent?.postMessage?.(
          {
            type: 'MFEStoreDispatch',
            data: {
              actionType: 'PaymentsMFEOmnioCheckPending',
              message: true,
            },
          },
          '*',
        )
      }

      dispatch(updateBalancePending(true))
      omnioBalanceCheck({
        cardNumber: trimmedCardNumber,
        CVC: cvvWatch,
        expiryMonth: expirationMonthWatch,
        expiryYear: `20${expirationYearWatch}`,
        amount: transactionDetails.overrideAmount || transactionDetails.amount,
      })
        .then(({ responseData }) => {
          const OMNIO_STATUSES = {
            error: 'error',
            success: 'success',
          }

          const { status, errorCode } = responseData

          if (status === OMNIO_STATUSES.success) {
            if (isPciFormEnabled) {
              window.parent?.postMessage?.(
                {
                  type: 'MFEStoreDispatch',
                  data: {
                    actionType: 'PaymentsMFEOmnioUpdateBalanceData',
                    message: responseData,
                  },
                },
                '*',
              )
            }

            dispatch(
              updateBalanceData({
                responseData,
              }),
            )
          } else {
            if (isPciFormEnabled) {
              window.parent?.postMessage?.(
                {
                  type: 'MFEStoreDispatch',
                  data: {
                    actionType: 'PaymentsMFEOmnioBalanceCheckError',
                    errorCode,
                  },
                },
                '*',
              )
            }
            dispatch(
              balanceCheckError({
                errorCode,
              }),
            )
          }
        })
        .finally(() => {
          if (isPciFormEnabled) {
            window.parent?.postMessage?.(
              {
                type: 'MFEStoreDispatch',
                data: {
                  actionType: 'PaymentsMFEOmnioCheckPending',
                  message: false,
                },
              },
              '*',
            )
          }
          dispatch(updateBalancePending(false))
        })
    }
  }

  const debouncedCheckOmnioData = debounce(checkOmnioData, 1000)

  useEffect(() => {
    debouncedCheckOmnioData()
  }, [cardNumberWatch, cvvWatch, expirationMonthWatch, expirationYearWatch])

  return (
    <>
      <h3 className="card-details__header medium nospace">
        {l10n.t('Card details')}
      </h3>
      <CardNumber
        watch={watch}
        cardBrand={cardBrand}
        setCardBrand={setCardBrand}
        register={register}
        setValue={setValue}
        errors={errors}
        trigger={trigger}
        control={control}
        getValues={getValues}
        loading={prePayment.isRequestPending}
      />
      {!hideCardHolderName && (
        <CardholderName
          register={register}
          setValue={setValue}
          errors={errors}
        />
      )}
      <ExpirationDate
        watch={watch}
        register={register}
        trigger={trigger}
        setValue={setValue}
        setFocus={setFocus}
        errors={errors}
        clearErrors={clearErrors}
      />
      <CVV
        register={register}
        errors={errors}
        trigger={trigger}
        cardBrand={cardBrand}
        setValue={setValue}
        cardType={cardType}
      />
      {!isMotoTransaction && !transactionDetails?.userEmail && (
        <Email
          register={register}
          errors={errors}
          label={l10n.t('Card holder email address')}
          type={CARD_FIELDS.email}
        />
      )}

      {canSaveCard && (
        <SaveCard control={control} shouldSaveCard={shouldSaveCard} />
      )}
    </>
  )
}

ManualCardForm.propTypes = {
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  setFocus: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  trigger: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  canSaveCard: PropTypes.bool,
  shouldSaveCard: PropTypes.bool,
}

export default ManualCardForm
